import React from "react";
import { useRouter } from "next/router";
import { useForm, Controller, NestedValue } from "react-hook-form";
import {
  Button,
  CircularProgress,
  Checkbox,
  Divider,
  FormControl,
  FormLabel,
  FormControlLabel,
  FormGroup,
  Grid,
  TextField,
  Box
} from "@material-ui/core";
import { Save as SaveIcon } from "@material-ui/icons";
import { Alert, Skeleton } from "@material-ui/lab";
import { makeStyles } from "@material-ui/core/styles";

import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { AsyncDropdown } from "src/apps/commons/dropdowns";
import { listAllPartners } from "src/apps/commons/dropdowns/api";
import { IPartnerBrief } from "../../partner/types";
import { partnerRoleMap } from "src/utils/enum";

import {
  IPartnerUser as Entity,
  IPartnerUserUpdateReq as SaveReq
} from "../types";
import { paths } from "../utils";

const useStyles = makeStyles({
  button: {
    paddingTop: "16px"
  },
  layoutButton: {
    flex: "1 1 auto"
  },
  skeleton: {
    display: "inline-flex",
    height: "72px",
    width: "100%"
  },
  alert: {
    alignItems: "center"
  }
});

interface Props {
  isLoading: boolean;
  isSaving: boolean;
  data?: Entity;
  onSubmit: (req: SaveReq) => void;
}

interface States {
  username: string;
  fullName: string;
  partnerDropdown: NestedValue<IDropdown<IPartnerBrief>>;
  active: boolean;
  roleAdmin: boolean;
  roleOfficer: boolean;
  roleSetting: boolean;
}

const schema = yup.object().shape({
  username: yup.string().trim().required("กรุณาระบุ Username"),
  fullName: yup.string().trim().required("กรุณาระบุ ชื่อ"),
  active: yup.boolean(),
  roleAdmin: yup.boolean(),
  roleOfficer: yup.boolean(),
  roleSetting: yup.boolean()
});

const CreateForm: React.FC<Props> = props => {
  const { isLoading, isSaving, data, onSubmit } = props;
  const classes = useStyles();
  const router = useRouter();

  const defaultFormData = React.useMemo<Partial<States>>(
    () => ({
      username: data?.username,
      fullName: data?.fullName,
      partnerDropdown: data?.partner
        ? ({
            value: data.partner.id,
            label: data.partner.label,
            meta: data.partner
          } as NestedValue<IDropdown<IPartnerBrief>>)
        : undefined,
      active: data?.active ?? true,
      roleAdmin: data?.roles?.includes("ROLE_ADMIN") ?? false,
      roleOfficer: data?.roles?.includes("ROLE_OFFICER") ?? false,
      roleSetting: data?.roles?.includes("ROLE_SETTING") ?? false
    }),
    [data]
  );

  const {
    handleSubmit,
    control,
    // watch,
    reset,
    formState: { errors }
  } = useForm<States>({
    defaultValues: defaultFormData,
    resolver: yupResolver(schema)
  });

  const handleTransformAndSubmit = (form: States) => {
    const roles: PartnerRole[] = [];
    if (form.roleAdmin) roles.push("ROLE_ADMIN");
    if (form.roleOfficer) roles.push("ROLE_OFFICER");
    if (form.roleSetting) roles.push("ROLE_SETTING");

    if (roles.length === 0) {
      return;
    }

    onSubmit({
      username: form.username,
      fullName: form.fullName,
      active: form.active,
      roles: roles
    });
  };

  // const checkedRoleAdmin = watch("roleAdmin");
  // const checkedRoleOfficer = watch("roleOfficer");
  // const checkedRoleSetting = watch("roleSetting");

  React.useEffect(() => {
    if (data) {
      reset(defaultFormData);
    }
  }, [data, defaultFormData, reset]);

  return (
    <form onSubmit={handleSubmit(handleTransformAndSubmit)}>
      <Grid container alignItems="flex-start" spacing={2}>
        <Grid item xs={12} md={6} lg={6}>
          {isLoading ? (
            <Skeleton className={classes.skeleton} />
          ) : (
            <Controller
              name="username"
              control={control}
              defaultValue={defaultFormData.username}
              render={({ field }) => (
                <TextField
                  variant="outlined"
                  fullWidth
                  disabled={isSaving}
                  error={!!errors.username}
                  helperText={errors.username?.message}
                  {...field}
                  label="Username *"
                />
              )}
            />
          )}
        </Grid>

        <Grid item xs={12} md={6} lg={6}>
          {isLoading ? (
            <Skeleton className={classes.skeleton} />
          ) : (
            <Controller
              name="fullName"
              control={control}
              defaultValue={defaultFormData.fullName}
              render={({ field }) => (
                <TextField
                  variant="outlined"
                  fullWidth
                  disabled={isSaving}
                  error={!!errors.fullName}
                  helperText={errors.fullName?.message}
                  {...field}
                  label="ชื่อ *"
                />
              )}
            />
          )}
        </Grid>

        <Grid item xs={12} md={6} lg={6}>
          {isLoading ? (
            <Skeleton className={classes.skeleton} />
          ) : (
            <Controller
              name="partnerDropdown"
              control={control}
              defaultValue={defaultFormData.partnerDropdown}
              render={({ field: { onChange, value } }) => (
                <AsyncDropdown
                  disabled
                  label={"Partner (เปลี่ยนไม่ได้)"}
                  fetchDropdown={listAllPartners}
                  error={Boolean(errors.partnerDropdown)}
                  helperText={errors.partnerDropdown?.message}
                  paginate={{
                    page: 1,
                    pageSize: 2000,
                    order: {
                      optionKey: "NAME",
                      desc: false
                    }
                  }}
                  additionalReqParams={{ excludeRemynd: true }}
                  onChange={onChange}
                  value={value}
                />
              )}
            />
          )}
        </Grid>

        <Grid item xs={12} md={6} lg={6}>
          <Box ml={1}>
            <FormControl component="fieldset">
              <FormLabel component="legend">
                Roles (เลือกอย่างน้อย 1) *
              </FormLabel>
              <FormGroup row>
                <FormControlLabel
                  label={partnerRoleMap["ROLE_ADMIN"]}
                  control={
                    <Controller
                      name="roleAdmin"
                      control={control}
                      defaultValue={defaultFormData.roleAdmin}
                      render={({ field: { value, onChange } }) => (
                        <Box ml={1}>
                          <Checkbox
                            color="primary"
                            checked={value}
                            onChange={onChange}
                            disabled={isSaving}
                          />
                        </Box>
                      )}
                    />
                  }
                />
                <FormControlLabel
                  label={partnerRoleMap["ROLE_OFFICER"]}
                  control={
                    <Controller
                      name="roleOfficer"
                      control={control}
                      defaultValue={defaultFormData.roleOfficer}
                      render={({ field: { value, onChange } }) => (
                        <Box ml={1}>
                          <Checkbox
                            color="primary"
                            checked={value}
                            onChange={onChange}
                            disabled={isSaving}
                          />
                        </Box>
                      )}
                    />
                  }
                />
                <FormControlLabel
                  label={partnerRoleMap["ROLE_SETTING"]}
                  control={
                    <Controller
                      name="roleSetting"
                      control={control}
                      defaultValue={defaultFormData.roleSetting}
                      render={({ field: { value, onChange } }) => (
                        <Box ml={1}>
                          <Checkbox
                            color="primary"
                            checked={value}
                            onChange={onChange}
                            disabled={isSaving}
                          />
                        </Box>
                      )}
                    />
                  }
                />
              </FormGroup>
            </FormControl>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Alert severity="info" className={classes.alert}>
            Admin สามารถเข้าจัดข้อมูลได้ทุกส่วน (User Product, จัดการข้อมูล
            Master, Partner User) <br /> Officer จัดการข้อมูล User Product
            <br />
            Setting จัดการข้อมูล Master ทั้งหมด
          </Alert>
        </Grid>
        {/* {checkedRoleAdmin == true ? (
          <Grid item xs={12}>
            <Alert severity="info">
              Admin สามารถเข้าจัดข้อมูลได้ทุกส่วน (User Product, จัดการข้อมูล
              Master, Partner User)
            </Alert>
          </Grid>
        ) : null}
        {checkedRoleOfficer == true ? (
          <Grid item xs={12}>
            <Alert severity="info">Officer จัดการข้อมูล User Product</Alert>
          </Grid>
        ) : null}
        {checkedRoleSetting == true ? (
          <Grid item xs={12}>
            <Alert severity="info">Setting จัดการข้อมูล Master ทั้งหมด</Alert>
          </Grid>
        ) : null} */}
        <Grid item>
          {isLoading ? (
            <Skeleton className={classes.skeleton} />
          ) : (
            <FormGroup row>
              <FormControlLabel
                control={
                  <Controller
                    name="active"
                    control={control}
                    defaultValue={defaultFormData.active}
                    render={({ field: { value, onChange } }) => (
                      <Box ml={1}>
                        <Checkbox
                          color="primary"
                          checked={value}
                          onChange={onChange}
                          disabled={isSaving}
                        />
                      </Box>
                    )}
                  />
                }
                label="Active"
              />
            </FormGroup>
          )}
        </Grid>

        <Grid item xs={12}>
          <Divider light />
        </Grid>

        <Grid item className={classes.layoutButton} />

        <Grid item className={classes.button}>
          <Button
            variant="contained"
            onClick={() => router.push(paths.index)}
            disabled={isSaving || isLoading}
          >
            Cancel
          </Button>
        </Grid>

        <Grid item className={classes.button}>
          <Button
            variant="contained"
            color="primary"
            disabled={isSaving || isLoading}
            type="submit"
            startIcon={
              isSaving ? (
                <CircularProgress color="secondary" size={20} />
              ) : (
                <SaveIcon />
              )
            }
          >
            Save
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default CreateForm;
